import React, { useEffect, useState } from 'react';
import { Label, Listbox, ListboxButton } from '@headlessui/react';
import { DesktopMenu } from './DesktopMenu';
import { MobileMenu } from './MobileMenu';
import { ColorSwatch } from './ColorSwatch';
import { useWindowSize } from '../../hooks/useWindowSize';
import { classNames } from '../../util/classNames';
import { SnapIcon } from '../SnapIcon';

export type SnapSelectMenuOptionType = {
  id: number;
  name: string;
  value?: string;
  selected: boolean;
  avatar?: string;
  selectAll?: boolean;
  label?: string;
  type?: 'option' | 'header';
  color?: string;
};

type Props = {
  label?: string;
  selectOptions: SnapSelectMenuOptionType[];
  onSelect?: (value: SnapSelectMenuOptionType | null) => Promise<void> | void;
  isRequired?: boolean;
  mobile?: boolean;
  mobileHeader: string;
  mobileFullHeight?: boolean;
  placeholder?: string;
  loading?: boolean;
  error?: {
    isError: boolean;
    errorMessage?: string;
    showErrorIcon?: boolean;
  };
};

const chevronDownIcon = (
  <svg
    fill='none'
    viewBox='0 0 24 24'
    stroke='currentColor'
    className='w-4 h-4 transform transition-transform duration-500 ease-in-out stroke-gray-500'
  >
    <title>chevronDownIcon</title>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='3.5'
      d='M19 9l-7 7-7-7'
    />
  </svg>
);

export const SnapSelectMenu = (props: Props) => {
  const {
    label,
    selectOptions,
    placeholder,
    isRequired = false,
    onSelect,
    mobileHeader,
    mobileFullHeight = false,
    loading = false,
    error = {
      isError: false,
      errorMessage: null,
      showErrorIcon: false,
    },
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { windowSize } = useWindowSize();

  useEffect(() => {
    setIsDesktop(windowSize.width >= 760);
  }, [windowSize]);

  const selected = selectOptions.find((so) => so.selected);

  const onChange = (value: SnapSelectMenuOptionType) => {
    setIsOpen(false);

    if (value.id === selected?.id) {
      return;
    }
    onSelect?.(value);
  };

  return (
    <div className='w-full'>
      <Listbox value={selected} onChange={onChange}>
        <div>
          {label ? (
            <Label className={'font-medium leading-5 mb-1.2 text-sm'}>
              {label}{' '}
              {isRequired ? <span className={'text-danger'}>*</span> : null}
            </Label>
          ) : null}
          <div className='relative'>
            <ListboxButton
              onClick={() => setIsOpen(!isOpen)}
              className={`flex justify-start h-[42px] w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left ring-1 ring-inset shadow-none ${
                error.isError ? 'ring-danger' : 'ring-gray-300'
              }  sm:text-sm sm:leading-6`}
            >
              <span className='flex truncate'>
                {selected?.color ? (
                  <ColorSwatch color={selected.color} />
                ) : null}
                {selected?.name || placeholder}
              </span>

              <span
                className={classNames(
                  'pointer-events-none absolute inset-y-0 right-0 flex items-center',
                  error.isError && error.showErrorIcon ? 'pr-6' : 'pr-3',
                )}
              >
                {chevronDownIcon}
              </span>
              {error.isError && error.showErrorIcon ? (
                <span className='absolute right-1 top-2 text-error'>
                  <SnapIcon icon='exclamation-circle' />
                </span>
              ) : null}
            </ListboxButton>
            {isDesktop ? (
              <DesktopMenu
                loading={loading}
                selectOptions={selectOptions}
                open={isOpen}
              />
            ) : null}
          </div>
          {error.isError && error.errorMessage && (
            <p className='ml-1 text-sm text-danger'>{error.errorMessage}</p>
          )}
        </div>
      </Listbox>

      {!isDesktop && (
        <MobileMenu
          loading={loading}
          onSelect={onChange}
          selectOptions={selectOptions}
          open={isOpen}
          setOpen={setIsOpen}
          header={mobileHeader}
          fullHeight={mobileFullHeight}
        />
      )}
    </div>
  );
};
